// Voice pages (agents, numbers, calls) — pure derivations shared by the three
// pages: what a call's tool calls mean, the « À vérifier » items and the
// figures of the state tiles. No React, no i18n: the pages word the items.
// Unit-tested in voiceModel.test.js on payloads shaped like the real API
// (Backend app/services/voice_agents_service.py, voice_numbers_service.py,
// app/api/ai_voice.py).
import { parseUtc } from '../../utils/dates';
import { TOOL_META } from '../../utils/voiceFormat';

// A line (number, agent, the whole account) that received calls and then
// none for this many days needs a person to call it: the voice gateway went
// silent for months once without anything else noticing (KI-164).
export const SILENT_DAYS = 7;
const DAY_MS = 24 * 60 * 60 * 1000;

export const daysSince = (value, now = Date.now()) => {
    const date = parseUtc(value);
    if (!date) return null;
    return Math.max(0, Math.floor((now - date.getTime()) / DAY_MS));
};

const latestOf = (values) => values.reduce((best, value) => {
    const date = parseUtc(value);
    if (!date) return best;
    return !best || date > parseUtc(best) ? value : best;
}, null);

// ------------------------------------------------------------ call outcomes

// Platform tools of the logistics voice agent (voice_tool_catalog.py) that
// utils/voiceFormat.js TOOL_META does not describe yet: without them a call
// that opened a ticket read « Info seule ».
const EXTRA_META = {
    create_support_ticket: { i18n: 'voiceTickets.outcomes.ticketCreated', fallback: 'Ticket créé', kind: 'action' },
    retrieve_knowledge: { i18n: 'voiceUi.outcomes.knowledge', fallback: 'Base de connaissances consultée', kind: 'lookup' },
    get_mondial_relay_return: { i18n: 'voiceUi.outcomes.relayReturn', fallback: 'Retour consulté', kind: 'lookup' },
    find_relay_points: { i18n: 'voiceUi.outcomes.relayPoints', fallback: 'Points relais recherchés', kind: 'lookup' },
};

export const TICKET_TOOLS = ['create_support_ticket', 'create_edn_ticket'];

export const toolMeta = (name) => {
    if (EXTRA_META[name]) return EXTRA_META[name];
    const meta = TOOL_META[name];
    if (!meta) return { kind: 'lookup' };
    return meta.key
        ? { i18n: `voiceTickets.outcomes.${meta.key}`, fallback: meta.fallback, kind: meta.kind }
        : { kind: meta.kind };
};

const prettify = (name) => String(name || '').replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());

export const toolOutcomeLabel = (name, t) => {
    const meta = toolMeta(name);
    return meta.i18n ? t(meta.i18n, meta.fallback) : prettify(name);
};

const KIND_ORDER = { action: 0, escalation: 1, lookup: 2 };

// Deduped outcomes of a call: actions, then hand-overs, then (optionally) lookups.
export const callOutcomes = (toolNames, t, { includeLookups = false } = {}) => {
    const seen = new Set();
    const outcomes = [];
    (toolNames || []).forEach((name) => {
        const { kind } = toolMeta(name);
        if (kind === 'ignore' || seen.has(name)) return;
        if (kind === 'lookup' && !includeLookups) return;
        seen.add(name);
        outcomes.push({ name, kind, label: toolOutcomeLabel(name, t) });
    });
    return outcomes.sort((a, b) => KIND_ORDER[a.kind] - KIND_ORDER[b.kind]);
};

// Did the agent DO something, hand the call over, or only inform?
export const callKind = (toolNames) => {
    const kinds = (toolNames || []).map((name) => toolMeta(name).kind);
    if (kinds.includes('action')) return 'action';
    if (kinds.includes('escalation')) return 'escalated';
    return 'info';
};

export const openedTicket = (toolNames) => (toolNames || []).some((name) => TICKET_TOOLS.includes(name));

// Figures over the calls the page holds (scope: the loaded rows).
export const summarizeCalls = (rows) => {
    const durations = rows.map((row) => row.duration_seconds).filter((value) => typeof value === 'number');
    const counts = { action: 0, escalated: 0, info: 0 };
    let tickets = 0;
    rows.forEach((row) => {
        counts[callKind(row.tool_names)] += 1;
        if (openedTicket(row.tool_names)) tickets += 1;
    });
    return {
        count: rows.length,
        avgDuration: durations.length ? durations.reduce((sum, value) => sum + value, 0) / durations.length : null,
        ...counts,
        tickets,
        latest: latestOf(rows.map((row) => row.started_at)),
    };
};

// The voice agents by id and by the numbers they answer: calls answered by a
// voice agent come back with assistant_name null (the backend resolves names
// among the text agents only), the agents list gives the name.
export const agentIndex = (agents) => {
    const byId = new Map();
    const byNumber = new Map();
    (agents || []).forEach((agent) => {
        byId.set(agent.id, agent);
        (agent.numbers || []).forEach((number) => byNumber.set(number.phone_number, agent));
    });
    return { byId, byNumber };
};

export const lineAgentName = (call, index) => (
    call.assistant_name
    || index.byId.get(call.assistant_id)?.name
    || index.byNumber.get(call.dialed_number)?.name
    || null
);

// ------------------------------------------------------------ agents

export const agentGaps = (agent) => [
    !agent.has_greeting && 'greeting',
    !agent.has_instructions && 'instructions',
    agent.voice_tools === 0 && 'tools',
].filter(Boolean);

export const agentReady = (agent) => agentGaps(agent).length === 0;

// GET /api/voice-agents → { agents, numbers, unrouted_numbers }.
export const agentsAttention = (data, now = Date.now()) => {
    if (!data) return [];
    const items = [];
    if (data.unrouted_numbers > 0) items.push({ key: 'unrouted', kind: 'unrouted', tone: 'warning', count: data.unrouted_numbers });
    (data.agents || []).forEach((agent) => {
        if (!(agent.numbers || []).length) return;
        const days = daysSince(agent.last_call_at, now);
        if (days !== null && days >= SILENT_DAYS) {
            items.push({ key: `silent-${agent.id}`, kind: 'silent', tone: 'warning', agent, days, last: agent.last_call_at });
        }
        const gaps = agentGaps(agent);
        if (gaps.length) items.push({ key: `gaps-${agent.id}`, kind: 'gaps', tone: 'warning', agent, gaps });
    });
    return items;
};

export const agentsFigures = (data) => {
    const agents = data?.agents || [];
    return {
        total: agents.length,
        live: agents.filter((agent) => (agent.numbers || []).length > 0).length,
        ready: agents.filter(agentReady).length,
        calls30d: agents.reduce((sum, agent) => sum + (agent.calls_30d || 0), 0),
        lastCall: latestOf(agents.map((agent) => agent.last_call_at)),
    };
};

// One agent's page: the saved agent (numbers, calls), the form being edited
// (instructions, tools) and the tool catalogue of the account.
export const agentPageAttention = ({ agent, form, tools }, now = Date.now()) => {
    if (!agent || !form) return [];
    const selected = new Set(form.selected_voice_tool_ids || []);
    const offeredActive = (tools || []).filter((tool) => tool.offered && selected.has(tool.id)).length;
    const unavailable = (tools || []).filter((tool) => !tool.offered && selected.has(tool.id)).length;
    const items = [];
    if (!(agent.numbers || []).length) items.push({ key: 'no-number', kind: 'noNumber', tone: 'warning' });
    const days = daysSince(agent.last_call_at, now);
    if ((agent.numbers || []).length && days !== null && days >= SILENT_DAYS) {
        items.push({ key: 'silent', kind: 'silent', tone: 'warning', days, last: agent.last_call_at });
    }
    if (!String(form.instructions || '').trim()) items.push({ key: 'no-instructions', kind: 'noInstructions', tone: 'warning' });
    if ((tools || []).length && offeredActive === 0) items.push({ key: 'no-tools', kind: 'noTools', tone: 'warning' });
    const knowledgeOn = (tools || []).some((tool) => tool.function_name === 'retrieve_knowledge' && selected.has(tool.id));
    if (knowledgeOn && !form.knowledge_agent_id) items.push({ key: 'no-knowledge', kind: 'noKnowledge', tone: 'warning' });
    if (unavailable > 0) items.push({ key: 'unavailable', kind: 'unavailableTools', tone: 'warning', count: unavailable });
    return items;
};

// ------------------------------------------------------------ numbers

export const isCanceling = (item) => Boolean(item.billing && (item.billing.cancel_at_period_end || item.billing.status === 'canceling'));

// GET /api/voice-numbers → { numbers, pending_checkout, paid_unassigned, has_billing, offer }.
export const numbersAttention = (view, now = Date.now()) => {
    const items = [];
    (view?.numbers || []).forEach((item) => {
        if (item.billing?.past_due) {
            items.push({ key: `past-due-${item.phone_number}`, kind: 'pastDue', tone: 'danger', number: item });
        }
        if (!item.agent) {
            items.push({ key: `no-agent-${item.phone_number}`, kind: 'noAgent', tone: 'warning', number: item, gone: Boolean(item.agent_missing) });
            return;
        }
        const days = daysSince(item.last_call_at, now);
        if (days !== null && days >= SILENT_DAYS) {
            items.push({ key: `silent-${item.phone_number}`, kind: 'silent', tone: 'warning', number: item, days, last: item.last_call_at });
        }
    });
    return items.sort((a, b) => (a.tone === b.tone ? 0 : a.tone === 'danger' ? -1 : 1));
};

export const numbersFigures = (view) => {
    const numbers = view?.numbers || [];
    const billed = numbers.filter((item) => item.billing && !isCanceling(item) && !item.billing.past_due);
    const periodEnds = billed.map((item) => parseUtc(item.billing.current_period_end)).filter(Boolean).sort((a, b) => a - b);
    return {
        total: numbers.length,
        answering: numbers.filter((item) => item.agent).length,
        withoutAgent: numbers.filter((item) => !item.agent).length,
        calls30d: numbers.reduce((sum, item) => sum + (item.calls_30d || 0), 0),
        lastCall: latestOf(numbers.map((item) => item.last_call_at)),
        subscriptions: billed.length,
        monthly: billed.reduce((sum, item) => sum + (item.billing.monthly_price_eur || 0), 0),
        nextRenewal: periodEnds.length ? periodEnds[0].toISOString() : null,
    };
};

// ------------------------------------------------------------ calls page

// The whole account silent (no date filter, newest call old), else each
// answering voice agent that went silent on its own.
export const callsAttention = ({ calls, filtered, agents }, now = Date.now()) => {
    if (!filtered && (calls || []).length) {
        const last = latestOf(calls.map((call) => call.started_at));
        const days = daysSince(last, now);
        if (days !== null && days >= SILENT_DAYS) return [{ key: 'silent-all', kind: 'silentAll', tone: 'warning', days, last }];
    }
    return agentsAttention({ agents: agents || [], unrouted_numbers: 0 }, now).filter((item) => item.kind === 'silent');
};
